import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: true,
    fallbackLng: 'ko',
    interpolation: {
      escapeValue: false,
    },
    resources: {
      en: {
        translation: {
          main_title: "ScreenFit",
          subtitle: "AI Body Measurement",
          upload_title: "Upload Your Photos (Up to 3)",
          upload_prompt: "Drag & drop images here, or click to select.",
          upload_guideline: "For best accuracy, upload at least one front and one side photo.",
          height_label: "Your Height (cm)",
          height_placeholder: "Enter height (100-230cm)",
          submit_button: "Calculate My Size",
          calculating: "Analyzing your photos... Please wait.",
          loading_tip1: "For the most accurate results, use photos taken from a distance in bright light.",
          loading_tip2: "Our AI is analyzing key points on your body to ensure precise measurements.",
          loading_tip3: "Getting the right fit online is tricky. We're here to help!",
          loading_tip4: "Did you know? Your body measurements can fluctuate throughout the day.",
          help_title: "What is ScreenFit?",
          help_p1: "Tired of clothes you bought online not fitting? Online shopping is convenient, but guessing the right size often leads to disappointment and hassle with returns.",
          help_p2: "ScreenFit solves this problem with AI. Just upload your photos, and our technology will analyze your body to provide accurate clothing size measurements. It's like having a personal tailor in your browser!",
          help_p3: "Get your personalized fit guide now and shop online with confidence!",
          error_title: "Calculation Failed",
          error_mediapipe: "Could not detect a person in the photo. Please try again with a clearer, full-body picture.",
          error_file: "Invalid file. Please upload a JPG or PNG file under 5MB.",
          error_file_limit: "You can upload a maximum of 3 images.",
          error_no_front_image: "Please select at least one 'Front' view image.",
          error_height: "Please enter a valid height between 100 and 230 cm.",
          result_title: "Your Body Measurements",
          result_item: "Item",
          result_measurement: "Measurement",
          result_confidence: "Confidence",
          top_measurements_title: "Top Measurements",
          bottom_measurements_title: "Bottom Measurements",
          shoulder_width: "Shoulder",
          sleeve_length: "Sleeve Length",
          torso_length: "Total Length",
          outseam: "Outseam",
          fit_guide_title: "Clothing Fit Recommendations",
          fit_guide_intro: "Based on your measurements, we recommend the following sizes for popular fits.",
          recommend_sleeve: "Sleeve Length",
          recommend_pants: "Pants Length",
          recommend_shoulder: "Shoulder Width",
          fit_style_sleeve_wrist: "Wrist Fit",
          fit_style_sleeve_hand: "Covers Hand Back",
          fit_style_sleeve_long: "Long / Oversized",
          fit_style_pants_ankle: "Ankle / Cropped",
          fit_style_pants_standard: "Standard / Regular",
          fit_style_pants_long: "Long / Stacked",
          fit_style_shoulder_regular: "Regular Fit",
          fit_style_shoulder_semi_over: "Semi-Oversized",
          fit_style_shoulder_over: "Oversized",
          copy_button: "Copy Results",
          save_button: "Save as Image",
          try_again_button: "Measure Again",
          copied_success: "Results copied to clipboard!",
          saved_success: "Image download started!",
          privacy_note: "Privacy Protected: Your photos are processed in your browser and are never uploaded to a server.",
          remove_button: "Remove",
          upload_more: "{{count}} more",
          accuracy_warning: "Low confidence score. Results may be inaccurate. Please try again with a clearer, full-body photo taken from further away.",
          brand_name: "Created by 김저녁 (evenight)",
          share_button: "Share",
          measurement_disclaimer: "Measurements are for reference only and may have a margin of error of 1-3cm.",
          share_success: "Result shared successfully!",
          share_error: "Could not share the result.",
          low_accuracy_or_missing_side_image: "Low accuracy or missing side photo",
          side_image_required_for_circumference: "Side photo was not recognized.",
          demo_mode_button: "Try with Demo Mode",
          adjust_mode_toggle: "Manual Coordinate Adjustment Mode",
          adjust_mode_active: "Landmark Editing Active (Drag dots to fine-tune)",
          calibration_title: "3D Camera Perspective Calibration",
          calibration_desc: "Match the 3D mannequin rotation with your photo's camera angle to calibrate perspective distortion.",
          pitch_label: "Camera Tilt (Pitch)",
          yaw_label: "Subject Angle (Yaw)",
          sample_title: "Try with Studio Model Photos (Real AI Scanner)",
          sample_desc: "Choose a pre-packaged studio model photo to run the actual MediaPipe AI pose estimator on standard and complex camera perspectives.",
          sample_male_front: "Male Model (Front View)",
          sample_female_front: "Female Model (Front View)",
          sample_male_yaw: "Male Model (20° Yaw Rotated)",
          sample_female_pitch: "Female Fitting Model (15° High Angle)",
          male: "Male",
          female: "Female",
          sample_male_front_desc: "176cm | Straight Pose",
          sample_female_front_desc: "165cm | Straight Pose",
          sample_male_yaw_desc: "178cm | Horizontally Rotated",
          sample_female_pitch_desc: "168cm | Vertically Tilted",
          sample_character: "Virtual Character (Fantasy Warrior)",
          sample_character_desc: "166cm | 2D Art Fit Analysis",
          sample_celebrity: "Celebrity Star (K-Pop Idol)",
          sample_celebrity_desc: "180cm | Outfit Fit Prediction",
          tab_casual: "Casual Photos",
          tab_character: "2D Characters",
          tab_celebrity: "Celebrities & Stars",
          analysis_complete: "Analysis Complete",
          demo_sample_preview: "Demo Sample Preview",
          preview_sample_locked: "Preview: Sample Locked",
          system_3d_calibration: "System: 3D Calibration",
          system_manual_edit: "System: Manual Dot Edit",
          system_locked_calibration: "System: Calibration Locked",
          pitch: "Pitch",
          yaw: "Yaw",
          dot_reset: "Reset Dots",
          angle_recalibrate: "Recalibrate Angle",
          creative_tips_title: "💡 Dynamic Proportions: What Else Can You Measure?",
          creative_tips_desc: "ScreenFit can be used in creative ways beyond standard personal sizing photos.",
          creative_tip1_title: "2D Characters & Virtual Avatars",
          creative_tip1_desc: "Curious about a game or webtoon character's clothing size? Upload the art and use 'Manual Coordinate Adjustment' to drag joint dots onto the illustration for a simulated fit recommendation.",
          creative_tip2_title: "Bias Celebrities & Stars",
          creative_tip2_desc: "Upload a full-body outfit photo of your favorite celebrity along with their profile height to estimate their shoulder width ratio and outseam proportions for styling ideas.",
        }
      },
      ko: {
        translation: {
          main_title: "스크린핏",
          subtitle: "AI 신체 치수 측정",
          upload_title: "사진 업로드 (최대 3장)",
          upload_prompt: "이곳에 사진들을 드래그하거나 클릭해서 선택하세요.",
          upload_guideline: "정확도를 위해, 정면 사진 1장과 측면 사진 1장 이상을 올려주세요.",
          height_label: "키 (cm)",
          height_placeholder: "키 입력 (100~230cm)",
          submit_button: "내 사이즈 계산하기",
          calculating: "사진들을 분석 중입니다... 잠시만 기다려주세요.",
          loading_tip1: "가장 정확한 결과를 위해, 밝은 조명 아래 멀리서 찍은 사진을 사용해 주세요.",
          loading_tip2: "AI가 신체의 주요 포인트를 분석하여 정확한 치수를 계산하고 있습니다.",
          loading_tip3: "온라인 쇼핑의 가장 큰 고민, 사이즈 문제를 해결해 드릴게요!",
          loading_tip4: "알고 계셨나요? 우리의 신체 사이즈는 하루 중에도 계속 변동될 수 있습니다.",
          help_title: "스크린핏이란?",
          help_p1: "온라인으로 주문한 옷이 막상 도착했을 때 사이즈가 맞지 않아 실망한 경험, 없으신가요? 온라인 쇼핑은 편리하지만, 사이즈 선택은 언제나 어려운 숙제입니다.",
          help_p2: "스크린핏은 AI 기술로 이 문제를 해결합니다. 사진을 업로드하기만 하면, AI가 신체를 분석하여 의류 사이즈 측정값을 제공합니다. 쉽고 편리하게 사이즈를 측정할 수 있습니다.",
          help_p3: "지금 바로 나만의 핏 가이드를 확인하고, 자신감 있게 온라인 쇼핑을 즐겨보세요!",
          error_title: "계산 실패",
          error_mediapipe: "사진에서 사람을 감지하지 못했습니다. 더 선명한 전신 사진으로 다시 시도해주세요.",
          error_file: "잘못된 파일입니다. 5MB 이하의 JPG 또는 PNG 파일을 업로드해주세요.",
          error_file_limit: "최대 3장의 이미지만 업로드할 수 있습니다.",
          error_no_front_image: "정면 사진을 찾을 수 없습니다. 더 선명한 정면 사진을 포함해주세요.",
          error_height: "100에서 230 사이의 유효한 키를 입력해주세요.",
          result_title: "내 의류 추천 사이즈",
          result_item: "측정 항목",
          result_measurement: "측정값",
          result_confidence: "신뢰도",
          top_measurements_title: "상의 추천 치수",
          bottom_measurements_title: "하의 추천 치수",
          shoulder_width: "어깨 단면",
          sleeve_length: "소매 길이",
          torso_length: "상의 총장",
          outseam: "다리 길이",
          fit_guide_title: "의류 핏 추천 가이드",
          fit_guide_intro: "측정된 사이즈를 기반으로, 스타일에 맞는 의류 사이즈를 추천해 드립니다.",
          recommend_sleeve: "추천 소매 길이",
          recommend_pants: "추천 바지 기장",
          recommend_shoulder: "추천 어깨너비",
          fit_style_sleeve_wrist: "손목 기장",
          fit_style_sleeve_hand: "손등 덮는 기장",
          fit_style_sleeve_long: "롱슬리브 / 오버핏",
          fit_style_pants_ankle: "발목 기장 / 크롭",
          fit_style_pants_standard: "기본 기장",
          fit_style_pants_long: "긴 기장 / 곱창핏",
          fit_style_shoulder_regular: "정사이즈 핏",
          fit_style_shoulder_semi_over: "세미 오버핏",
          fit_style_shoulder_over: "오버사이즈 핏",
          copy_button: "결과 복사",
          save_button: "이미지 저장",
          try_again_button: "다시 측정하기",
          copied_success: "결과가 클립보드에 복사되었습니다!",
          saved_success: "이미지 다운로드를 시작합니다!",
          privacy_note: "개인정보 보호: 사진은 서버에 업로드되지 않고, 사용자의 브라우저 안에서만 처리됩니다.",
          remove_button: "삭제",
          upload_more: "{{count}}장 더",
          accuracy_warning: "신뢰도 점수가 낮습니다. 결과가 부정확할 수 있습니다. 더 선명하고, 멀리서 찍은 전신 사진으로 다시 시도해주세요.",
          brand_name: "제작자: 김저녁 (evenight)",
          share_button: "공유하기",
          measurement_disclaimer: "측정 결과는 참고용이며, 실제 사이즈와 1-3cm의 오차가 있을 수 있습니다.",
          share_success: "결과를 공유했습니다!",
          share_error: "결과를 공유할 수 없습니다.",
          low_accuracy_or_missing_side_image: "측면 사진 없음 또는 정확도 낮음",
          side_image_required_for_circumference: "측면 사진이 인식되지 않았습니다.",
          demo_mode_button: "체험용 데모로 시작하기",
          adjust_mode_toggle: "수동 관절 좌표 미세조정 모드",
          adjust_mode_active: "관절 세부 조절 활성화됨 (도트를 드래그하세요)",
          calibration_title: "3D 카메라 원근 왜곡 보정 (Calibration)",
          calibration_desc: "사진이 촬영된 앵글에 맞게 3D 마네킹을 회전시켜 왜곡률을 정밀 보정하세요.",
          pitch_label: "상하 촬영 앵글 (Pitch)",
          yaw_label: "피사체 회전 각도 (Yaw)",
          sample_title: "스튜디오 피팅 모델 샘플로 테스트 (실제 AI 구동)",
          sample_desc: "실제 미디어파이프 AI가 정면 및 왜곡 각도 카메라 앵글을 인식하는 과정을 스튜디오 샘플 사진으로 직접 테스트해 보세요.",
          sample_male_front: "남성 피팅 모델 (정면 뷰)",
          sample_female_front: "여성 피팅 모델 (정면 뷰)",
          sample_male_yaw: "남성 피팅 모델 (20° 가로 회전 왜곡)",
          sample_female_pitch: "여성 피팅 모델 (15° 하이 앵글 원근 왜곡)",
          male: "남성",
          female: "여성",
          sample_male_front_desc: "176cm | 정면 피팅",
          sample_female_front_desc: "165cm | 정면 피팅",
          sample_male_yaw_desc: "178cm | 가로 각도 왜곡 보정",
          sample_female_pitch_desc: "168cm | 세로 각도 왜곡 보정",
          sample_character: "가상 캐릭터 (판타지 전사)",
          sample_character_desc: "166cm | 2D 일러스트 핏 분석",
          sample_celebrity: "연예인 스타 (K-Pop 아이돌)",
          sample_celebrity_desc: "180cm | 레드카펫 코디 핏 예측",
          tab_casual: "일상 사진",
          tab_character: "가상 캐릭터 (2D)",
          tab_celebrity: "연예인 / 스타",
          analysis_complete: "신체 분석 완료",
          demo_sample_preview: "데모 결과 미리보기",
          preview_sample_locked: "미리보기: 샘플 측정값",
          system_3d_calibration: "시스템: 3D 왜곡 보정",
          system_manual_edit: "시스템: 수동 도트 편집",
          system_locked_calibration: "시스템: 왜곡 보정 완료",
          pitch: "상하 각도 (Pitch)",
          yaw: "좌우 각도 (Yaw)",
          dot_reset: "도트 초기화",
          angle_recalibrate: "각도 재보정",
          creative_tips_title: "💡 이런 사진도 측정할 수 있어요! (이색 활용 팁)",
          creative_tips_desc: "스크린핏은 본인의 전신 사진 외에도 다양한 창의적인 방식으로 시뮬레이션할 수 있습니다.",
          creative_tip1_title: "2D 캐릭터 & 가상 아바타 비율 분석",
          creative_tip1_desc: "좋아하는 웹툰이나 애니메이션 일러스트의 실제 의류 추천 기장이 궁금한가요? 일러스트를 올리고 AI 검출이 미흡할 땐 '수동 미세조정 모드'로 관절 점들을 직접 어깨, 소매 끝에 맞춤 조절하여 가상 사이즈를 시뮬레이션할 수 있습니다.",
          creative_tip2_title: "최애 연예인 & 스타 비율 예측",
          creative_tip2_desc: "좋아하는 가수나 배우의 전신 무대 컷, 직찍 사진을 프로필상의 공식 키와 함께 입력하면, 스타의 실제 어깨 너비나 다리 기장(아웃심) 비율을 역산해 볼 수 있어 구매 핏 매칭 가이드로 재미있게 활용할 수 있습니다.",
        }
      },
    }
  });

export default i18n;